/**
 * 메뉴 관리 — 사용자 사이트 메뉴 메타를 DB(admin_menu_registry)로 관리.
 * 추가/편집/삭제/노출 토글 + 모듈 매핑 + 시각적 아이콘 피커(내장 lucide + 커스텀 SVG).
 *
 * 2026-08-11 — 노출 토글의 사정거리가 넓어졌다(사용자 요구):
 *   ① 좌측 사이드바  ② 하단 상태바 항목(채팅·메일·전자결재)  ③ **데이터 조회까지 차단**
 *   (chat-server `menu-registry-guard` 가 끈 메뉴의 도메인 API 를 404 로 막는다. 반영은
 *    캐시 TTL 때문에 최대 1분 지연.)
 */
import { useCallback, useEffect, useState } from 'react';
import './ServiceModules.css';
import { Trash2, X, Plus } from '../../../icons';
import {
  listMenus, createMenu, updateMenu, deleteMenu,
  type MenuRow, type MenuInput,
} from '../../../api/menus';
import { RenderIcon, StyledSelect, IconPicker, type SelectOption } from './MenuControls';
import { useCustomIcons } from './iconStore';

const SECTION_OPTS: SelectOption[] = [
  { value: 'primary', label: '좌측 레일 · 상단(주 메뉴)' },
  { value: 'secondary', label: '좌측 레일 · 하단(설정 영역)' },
  // 2026-08-11 — 사용자 화면 하단 상태바 자리. 좌측 레일에는 뜨지 않는다.
  { value: 'status', label: '하단 상태바' },
  // 2026-08-19 (사용자 지시) — 보조영역. 화면(좌측 레일·상태바)에는 뜨지 않고 **전체메뉴에만** 보인다.
  //   자주 쓰지 않는 메뉴를 레일에서 내리되 접근 경로는 남겨 두기 위한 자리다. 사용자는
  //   전체메뉴에서 별(★)을 눌러 자기 우측 레일에 직접 고정할 수 있다.
  { value: 'aux', label: '보조영역(전체메뉴에만 표시)' },
  // 2026-09-15 (REQUEST-6 R6-12 사용자 결정) — 앱 전용. 모바일 앱에만 뜨고 사용자 사이트(웹)에는 어디에도 안 뜬다.
  //   ⚠ 웹이 이 위치를 거르는 판(lib/menu-config isWebMenuRow)이 배포된 뒤에만 이 위치로 행을 만든다 — 아니면 레일 상단에 뜬다.
  { value: 'mobile', label: '앱 전용(모바일 앱에만 표시)' },
];

/** 카드 칩에 표시할 위치 이름. */
function sectionLabel(section: string): string {
  if (section === 'secondary') return '레일 하단';
  if (section === 'status') return '상태바';
  if (section === 'aux') return '보조영역';
  if (section === 'mobile') return '앱 전용';
  return '레일 상단';
}
const MODULE_OPTS: SelectOption[] = [
  { value: '', label: '(매핑 없음)' },
  { value: 'mail', label: 'mail (메일)' },
  { value: 'calendar', label: 'calendar (캘린더)' },
  { value: 'task', label: 'task (할일)' },
  { value: 'workspace', label: 'workspace (워크스페이스)' },
  { value: 'workchat', label: 'workchat (채팅)' },
];

function Switch({ checked, disabled, onChange, label }: {
  checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string;
}) {
  return (
    <label className="svc-switch" title={label}>
      <input type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span className="svc-switch__track" />
      <span className="svc-switch__thumb" />
    </label>
  );
}

export function MenuRegistrySection() {
  const [list, setList] = useState<MenuRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<MenuRow | null>(null);
  // 커스텀 아이콘 — 공유 스토어(아이콘 관리에서 추가 시 즉시 반영).
  const { customSvg, customNames } = useCustomIcons();

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await listMenus()); }
    catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? '메뉴 목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function toggle(m: MenuRow) {
    setBusyId(m.id);
    try {
      const u = await updateMenu(m.id, { enabled: !m.enabled });
      setList((arr) => arr.map((x) => (x.id === m.id ? u : x)));
    } catch (e: any) { setError(e?.response?.data?.error ?? '변경 실패'); }
    finally { setBusyId(null); }
  }
  async function remove(m: MenuRow) {
    if (!window.confirm(`메뉴 "${m.label}"(${m.url})를 삭제할까요?`)) return;
    setBusyId(m.id);
    try { await deleteMenu(m.id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <section className="svc-cat svc-cat--menu">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><RenderIcon name="LayoutGrid" size={17} /></span>
        <span className="svc-cat__title">메뉴 관리</span>
        <span className="svc-cat__count">{list.length}</span>
        <span className="svc-cat__desc">사용자 사이트 메뉴(URL·라벨·아이콘·노출·모듈매핑) — 끄면 사이드바·상태바에서 숨기고 데이터 조회도 차단</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <button type="button" className="svc-add" onClick={() => { setEditing(null); setShowModal(true); }}>
        <span className="svc-add__icon"><Plus size={20} /></span>
        <span>
          <span className="svc-add__title">메뉴 추가</span>
          <span className="svc-add__desc">새 사이드바 메뉴 항목을 등록합니다(URL·라벨·아이콘 지정).</span>
        </span>
      </button>

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : list.length === 0 ? (
        <p className="svc-empty">등록된 메뉴가 없습니다.</p>
      ) : (
        <div className="svc-grid">
          {list.map((m) => {
            const busy = busyId === m.id;
            return (
              <div key={m.id} className={`svc-card${m.enabled ? '' : ' is-off'}`}>
                <div className="svc-card__top">
                  <span className="svc-card__icon"><RenderIcon name={m.icon} size={20} customSvg={customSvg} /></span>
                  <span className="svc-card__name" title={m.label}>{m.label}</span>
                  <Switch checked={m.enabled} disabled={busy} onChange={() => void toggle(m)} label={m.enabled ? '숨기기' : '표시'} />
                </div>
                <div className="svc-card__detail">{m.url}</div>
                <div className="svc-card__chips">
                  <span className="svc-card__chip">{m.menu_key}</span>
                  <span className="svc-card__chip">{sectionLabel(m.section)}</span>
                  {m.module_key
                    ? <span className="svc-card__chip">모듈: {m.module_key}</span>
                    : <span className="svc-card__chip">모듈 매핑 없음</span>}
                  {m.is_external && <span className="svc-card__chip">외부링크</span>}
                </div>
                <div className="svc-card__foot">
                  <span className="svc-card__meta">정렬 {m.sort_order}</span>
                  <button type="button" className="svc-btn" style={{ marginLeft: 'auto' }} onClick={() => { setEditing(m); setShowModal(true); }}>편집</button>
                  <button type="button" className="svc-btn svc-btn--danger" disabled={busy} onClick={() => void remove(m)} aria-label="삭제">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <MenuModal
          existing={editing}
          customSvg={customSvg}
          customNames={customNames}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); void reload(); }}
        />
      )}
    </section>
  );
}

/**
 * ⚠ 2026-09-09 — `MenuListSection`(새 목록 규격 화면)이 **같은 편집 폼을 그대로 쓴다**.
 *   폼을 베껴 두 벌로 만들면 아이콘 피커·모듈 매핑이 갈라지므로 여기서 내보낸다.
 *   이 파일의 나머지(카드 격자 화면)는 되돌리기 대비로 손대지 않고 남겨 둔다.
 */
export function MenuModal({ existing, customSvg, customNames, onClose, onSaved }: {
  existing: MenuRow | null;
  customSvg: Record<string, string>;
  customNames: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [menuKey, setMenuKey] = useState(existing?.menu_key ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [icon, setIcon] = useState(existing?.icon ?? 'LayoutGrid');
  const [section, setSection] = useState(existing?.section ?? 'primary');
  const [moduleKey, setModuleKey] = useState(existing?.module_key ?? '');
  const [sortOrder, setSortOrder] = useState(existing?.sort_order ?? 100);
  const [isExternal, setIsExternal] = useState(existing?.is_external ?? false);
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const canSubmit = menuKey.trim() && url.trim() && label.trim();

  async function save() {
    setSaving(true); setErr(null);
    try {
      const payload: MenuInput = {
        url: url.trim(), label: label.trim(), icon, section,
        module_key: moduleKey || null, sort_order: Number(sortOrder) || 100,
        is_external: isExternal, enabled,
      };
      if (existing) await updateMenu(existing.id, payload);
      else await createMenu({ ...payload, menu_key: menuKey.trim() });
      onSaved();
    } catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? '저장 실패'); }
    finally { setSaving(false); }
  }

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal" role="dialog" aria-modal="true" aria-label="메뉴" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3>{existing ? '메뉴 편집' : '메뉴 추가'}</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          {/* 미리보기 */}
          <div className="svc-menuprev">
            <span className="svc-menuprev__icon"><RenderIcon name={icon} size={18} customSvg={customSvg} /></span>
            <span className="svc-menuprev__label">{label || '메뉴 미리보기'}</span>
            <span className="svc-menuprev__url">{url || '/url'}</span>
          </div>

          <div className="svc-row2">
            <div className="svc-field">
              <label>메뉴 키 *</label>
              <input value={menuKey} disabled={!!existing} placeholder="예: report" onChange={(e) => setMenuKey(e.target.value)} />
              {existing && <span className="svc-field__hint">키는 변경할 수 없습니다.</span>}
            </div>
            <div className="svc-field">
              <label>정렬 순서</label>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </div>
          </div>
          <div className="svc-field">
            <label>라벨 *</label>
            <input value={label} placeholder="예: 보고서" onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="svc-field">
            <label>URL *</label>
            <input value={url} placeholder="/report 또는 https://외부주소" onChange={(e) => setUrl(e.target.value)} />
            <span className="svc-field__hint">내부 메뉴는 8443 SPA 에 해당 라우트/페이지가 있어야 화면이 뜹니다.</span>
          </div>
          <div className="svc-field">
            <label>아이콘</label>
            <IconPicker value={icon} onChange={setIcon} customSvg={customSvg} customNames={customNames} />
          </div>
          <div className="svc-row2">
            <div className="svc-field">
              <label>위치</label>
              <StyledSelect value={section} options={SECTION_OPTS} onChange={setSection} />
              {section === 'aux' && (
                <span className="svc-field__hint">
                  좌측 레일에는 표시되지 않고 <b>전체메뉴</b>에서만 보입니다. 사용자가 전체메뉴에서
                  별(★)을 눌러 자기 우측 레일에 고정할 수 있습니다.
                </span>
              )}
            </div>
            <div className="svc-field">
              <label>모듈 매핑 (선택)</label>
              <StyledSelect value={moduleKey ?? ''} options={MODULE_OPTS} onChange={setModuleKey} />
              <span className="svc-field__hint">매핑하면 회사별 서비스 토글과 연동됩니다.</span>
            </div>
          </div>
          <div className="svc-row2">
            <div className="svc-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                외부 링크
                <Switch checked={isExternal} onChange={setIsExternal} label="외부 링크" />
              </label>
            </div>
            <div className="svc-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                노출
                <Switch checked={enabled} onChange={setEnabled} label="노출" />
              </label>
            </div>
          </div>
          {err && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{err}</div>}
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" onClick={() => void save()} disabled={!canSubmit || saving}>
            {saving ? '저장 중…' : existing ? '저장' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}
